import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './NotizenAufgaben.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Notiz {
  notiz_id: number;
  entitaet_typ: string;
  entitaet_id: number;
  text: string;
  autor_id: number | null;
  autor_name: string | null;
  mentions: number[];
  erstellt_am: string | null;
}

interface Aufgabe {
  aufgabe_id: number;
  entitaet_typ: string;
  entitaet_id: number;
  titel: string;
  beschreibung: string | null;
  faellig_am: string | null;
  zugewiesen_an: number | null;
  status: 'Offen' | 'In Arbeit' | 'Erledigt';
  erstellt_von: number | null;
  erstellt_am: string | null;
  erledigt_am: string | null;
}

interface NotizenAufgabenProps {
  entitaetTyp: string;
  entitaetId: number;
  authToken: string;
  onChange?: () => void;
}

function formatDatum(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function highlightMentions(text: string): React.ReactNode {
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) =>
    /^@\w+$/.test(part)
      ? <span key={i} className="na-mention">{part}</span>
      : part
  );
}

const STATUS_COLOR: Record<string, string> = {
  'Offen': 'var(--gold)',
  'In Arbeit': 'var(--green-900)',
  'Erledigt': 'var(--green-500)',
};

const NotizenAufgaben: React.FC<NotizenAufgabenProps> = ({ entitaetTyp, entitaetId, authToken, onChange }) => {
  const [activeTab, setActiveTab] = useState<'notizen' | 'aufgaben'>('notizen');

  // Notizen state
  const [notizen, setNotizen] = useState<Notiz[]>([]);
  const [notizenLoading, setNotizenLoading] = useState(false);
  const [notizenError, setNotizenError] = useState('');
  const [neueNotiz, setNeueNotiz] = useState('');
  const [notizSubmitting, setNotizSubmitting] = useState(false);

  // Aufgaben state
  const [aufgaben, setAufgaben] = useState<Aufgabe[]>([]);
  const [aufgabenLoading, setAufgabenLoading] = useState(false);
  const [aufgabenError, setAufgabenError] = useState('');
  const [neueAufgabeTitel, setNeueAufgabeTitel] = useState('');
  const [neueAufgabeBeschreibung, setNeueAufgabeBeschreibung] = useState('');
  const [neueAufgabeFaellig, setNeueAufgabeFaellig] = useState('');
  const [aufgabeSubmitting, setAufgabeSubmitting] = useState(false);

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const loadNotizen = useCallback(async () => {
    setNotizenLoading(true);
    setNotizenError('');
    try {
      const res = await axios.get(
        `${API_URL}/api/notizen?entitaet_typ=${entitaetTyp}&entitaet_id=${entitaetId}`,
        { headers }
      );
      setNotizen(res.data);
    } catch {
      setNotizenError('Notizen konnten nicht geladen werden.');
    } finally {
      setNotizenLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitaetTyp, entitaetId, authToken]);

  const loadAufgaben = useCallback(async () => {
    setAufgabenLoading(true);
    setAufgabenError('');
    try {
      const res = await axios.get(
        `${API_URL}/api/aufgaben?entitaet_typ=${entitaetTyp}&entitaet_id=${entitaetId}`,
        { headers }
      );
      setAufgaben(res.data);
    } catch {
      setAufgabenError('Aufgaben konnten nicht geladen werden.');
    } finally {
      setAufgabenLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitaetTyp, entitaetId, authToken]);

  useEffect(() => { loadNotizen(); }, [loadNotizen]);
  useEffect(() => { loadAufgaben(); }, [loadAufgaben]);

  const deleteNotiz = async (notizId: number) => {
    try {
      await axios.delete(`${API_URL}/api/notizen/${notizId}`, { headers });
      loadNotizen();
      onChange?.();
    } catch {
      setNotizenError('Notiz konnte nicht gelöscht werden.');
    }
  };

  const submitNotiz = async () => {
    if (!neueNotiz.trim()) return;
    setNotizSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/notizen`,
        { entitaet_typ: entitaetTyp, entitaet_id: entitaetId, text: neueNotiz.trim() },
        { headers }
      );
      setNeueNotiz('');
      loadNotizen();
      onChange?.();
    } catch {
      setNotizenError('Notiz konnte nicht gespeichert werden.');
    } finally {
      setNotizSubmitting(false);
    }
  };

  const submitAufgabe = async () => {
    if (!neueAufgabeTitel.trim()) return;
    setAufgabeSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/aufgaben`,
        {
          entitaet_typ: entitaetTyp,
          entitaet_id: entitaetId,
          titel: neueAufgabeTitel.trim(),
          beschreibung: neueAufgabeBeschreibung.trim() || null,
          faellig_am: neueAufgabeFaellig || null,
        },
        { headers }
      );
      setNeueAufgabeTitel('');
      setNeueAufgabeBeschreibung('');
      setNeueAufgabeFaellig('');
      loadAufgaben();
      onChange?.();
    } catch {
      setAufgabenError('Aufgabe konnte nicht gespeichert werden.');
    } finally {
      setAufgabeSubmitting(false);
    }
  };

  const updateStatus = async (aufgabeId: number, status: string) => {
    try {
      await axios.put(
        `${API_URL}/api/aufgaben/${aufgabeId}/status`,
        { status },
        { headers }
      );
      loadAufgaben();
      onChange?.();
    } catch {
      setAufgabenError('Status konnte nicht aktualisiert werden.');
    }
  };

  return (
    <div className="na-container">
      <div className="na-tabs">
        <button type="button"
          className={`na-tab ${activeTab === 'notizen' ? 'active' : ''}`}
          onClick={() => setActiveTab('notizen')}
        >
          Notizen {notizen.length > 0 && <span className="na-badge">{notizen.length}</span>}
        </button>
        <button type="button"
          className={`na-tab ${activeTab === 'aufgaben' ? 'active' : ''}`}
          onClick={() => setActiveTab('aufgaben')}
        >
          Aufgaben {aufgaben.length > 0 && <span className="na-badge">{aufgaben.length}</span>}
        </button>
      </div>

      {/* NOTIZEN TAB */}
      {activeTab === 'notizen' && (
        <div className="na-panel">
          {notizenError && <div className="na-error">{notizenError}</div>}

          <div className="na-new-form">
            <textarea
              className="na-textarea"
              placeholder="Neue Notiz schreiben… @Name für Erwähnungen"
              value={neueNotiz}
              onChange={e => setNeueNotiz(e.target.value)}
              rows={3}
            />
            <button type="button"
              className="na-btn primary"
              onClick={submitNotiz}
              disabled={notizSubmitting || !neueNotiz.trim()}
            >
              {notizSubmitting ? 'Speichern…' : 'Notiz speichern'}
            </button>
          </div>

          {notizenLoading ? (
            <div className="na-loading">Notizen werden geladen…</div>
          ) : notizen.length === 0 ? (
            <div className="na-empty">
              
              <p>Noch keine Notizen vorhanden.</p>
            </div>
          ) : (
            <div className="na-list">
              {notizen.map(n => (
                <div key={n.notiz_id} className="na-item">
                  <div className="na-item-header">
                    <span className="na-autor">{n.autor_name || 'Unbekannt'}</span>
                    <span className="na-datum">{formatDatum(n.erstellt_am)}</span>
                    <button type="button"
                      className="na-btn small na-delete-btn"
                      onClick={() => deleteNotiz(n.notiz_id)}
                      title="Notiz löschen"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="na-text">{highlightMentions(n.text)}</div>
                  {n.mentions && n.mentions.length > 0 && (
                    <div className="na-mentions-info">
                      {n.mentions.length} Benutzer erwähnt
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* AUFGABEN TAB */}
      {activeTab === 'aufgaben' && (
        <div className="na-panel">
          {aufgabenError && <div className="na-error">{aufgabenError}</div>}

          <div className="na-new-form">
            <input
              className="na-input"
              type="text"
              placeholder="Aufgabentitel *"
              value={neueAufgabeTitel}
              onChange={e => setNeueAufgabeTitel(e.target.value)}
            />
            <textarea
              className="na-textarea"
              placeholder="Beschreibung (optional)"
              value={neueAufgabeBeschreibung}
              onChange={e => setNeueAufgabeBeschreibung(e.target.value)}
              rows={2}
            />
            <div className="na-form-row">
              <label className="na-label">
                Fällig am
                <input
                  className="na-input"
                  type="datetime-local"
                  value={neueAufgabeFaellig}
                  onChange={e => setNeueAufgabeFaellig(e.target.value)}
                />
              </label>
              <button type="button"
                className="na-btn primary"
                onClick={submitAufgabe}
                disabled={aufgabeSubmitting || !neueAufgabeTitel.trim()}
              >
                {aufgabeSubmitting ? 'Speichern…' : 'Aufgabe erstellen'}
              </button>
            </div>
          </div>

          {aufgabenLoading ? (
            <div className="na-loading">Aufgaben werden geladen…</div>
          ) : aufgaben.length === 0 ? (
            <div className="na-empty">
              
              <p>Noch keine Aufgaben vorhanden.</p>
            </div>
          ) : (
            <div className="na-list">
              {aufgaben.map(a => (
                <div key={a.aufgabe_id} className={`na-item aufgabe ${a.status === 'Erledigt' ? 'erledigt' : ''}`}>
                  <div className="na-item-header">
                    <span className="na-titel">{a.titel}</span>
                    <span
                      className="na-status-badge"
                      style={{ background: STATUS_COLOR[a.status] || 'var(--ink-2)' }}
                    >
                      {a.status}
                    </span>
                  </div>
                  {a.beschreibung && (
                    <div className="na-text">{a.beschreibung}</div>
                  )}
                  <div className="na-item-footer">
                    {a.faellig_am && (
                      <span className={`na-faellig ${new Date(a.faellig_am) < new Date() && a.status !== 'Erledigt' ? 'ueberfaellig' : ''}`}>
                        Fällig: {formatDatum(a.faellig_am)}
                      </span>
                    )}
                    <div className="na-status-actions">
                      {a.status !== 'Offen' && (
                        <button type="button" className="na-btn small" onClick={() => updateStatus(a.aufgabe_id, 'Offen')}>
                          Offen
                        </button>
                      )}
                      {a.status !== 'In Arbeit' && (
                        <button type="button" className="na-btn small secondary" onClick={() => updateStatus(a.aufgabe_id, 'In Arbeit')}>
                          In Arbeit
                        </button>
                      )}
                      {a.status !== 'Erledigt' && (
                        <button type="button" className="na-btn small success" onClick={() => updateStatus(a.aufgabe_id, 'Erledigt')}>
                          Erledigt
                        </button>
                      )}
                    </div>
                  </div>
                  {a.erledigt_am && (
                    <div className="na-erledigt">Erledigt: {formatDatum(a.erledigt_am)}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotizenAufgaben;
